import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function Investments() {
  const [investments, setInvestments] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    account_id: '',
    investment_type: 'stocks',
    amount: '',
    description: ''
  });

  // Conditions fixées par la banque
  const investmentConditions = {
    stocks: { 
      maturity_months: 60, 
      interest_rate: 8.5, 
      description: 'Investissement en actions avec potentiel de croissance élevé',
      min_amount: 100
    },
    bonds: { 
      maturity_months: 120, 
      interest_rate: 4.2, 
      description: 'Obligations d\'État et corporate sécurisées',
      min_amount: 500
    },
    funds: { 
      maturity_months: 36, 
      interest_rate: 6.8, 
      description: 'Fonds communs de placement diversifiés',
      min_amount: 250
    },
    crypto: { 
      maturity_months: 24, 
      interest_rate: 12.5, 
      description: 'Investissement crypto avec volatilité élevée',
      min_amount: 50
    },
    term_deposit: { 
      maturity_months: 12, 
      interest_rate: 3.5, 
      description: 'Compte à terme garanti avec taux fixe',
      min_amount: 1000
    },
    real_estate: { 
      maturity_months: 240, 
      interest_rate: 5.5, 
      description: 'Investissement immobilier à long terme',
      min_amount: 5000
    }
  };

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [invRes, accRes] = await Promise.all([
        axios.get(`${API_URL}/investments`, config),
        axios.get(`${API_URL}/accounts`, config)
      ]);
      setInvestments(invRes.data);
      setAccounts(accRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const conditions = investmentConditions[formData.investment_type];
      
      // Vérifier le montant minimum
      if (parseFloat(formData.amount) < conditions.min_amount) {
        alert(`Montant minimum pour ce type d'investissement : ${conditions.min_amount}€`);
        return;
      }
      
      await axios.post(`${API_URL}/investments`, {
        ...formData,
        amount: parseFloat(formData.amount),
        maturity_months: conditions.maturity_months,
        interest_rate: conditions.interest_rate,
        description: formData.description || conditions.description
      }, config);
      alert('Investissement créé avec succès !');
      setShowForm(false);
      setFormData({ account_id: '', investment_type: 'stocks', amount: '', description: '' });
      loadData();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return <div className="text-center py-20">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Investissements</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          {showForm ? 'Annuler' : 'Nouvel investissement'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Créer un investissement</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Compte source
              </label>
              <select
                value={formData.account_id}
                onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Sélectionner un compte</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.account_type} - {acc.balance.toFixed(2)} €
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type d'investissement *
              </label>
              <select
                value={formData.investment_type}
                onChange={(e) => setFormData({ ...formData, investment_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="stocks">Actions</option>
                <option value="bonds">Obligations</option>
                <option value="funds">Fonds communs</option>
                <option value="crypto">Cryptomonnaies</option>
                <option value="term_deposit">Compte à terme</option>
                <option value="real_estate">Immobilier</option>
              </select>
            </div>

            {/* Conditions de la banque - Lecture seule */}
            <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
              <p className="text-sm font-semibold text-green-900 mb-3">📊 Conditions Bank Kreiz Breizh</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-600">Taux d'intérêt</p>
                  <p className="text-2xl font-bold text-green-600">
                    {investmentConditions[formData.investment_type].interest_rate}%
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Échéance</p>
                  <p className="text-2xl font-bold text-green-600">
                    {investmentConditions[formData.investment_type].maturity_months} mois
                  </p>
                  <p className="text-xs text-gray-500">
                    ({Math.round(investmentConditions[formData.investment_type].maturity_months / 12)} an{investmentConditions[formData.investment_type].maturity_months / 12 > 1 ? 's' : ''})
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Montant minimum</p>
                  <p className="text-2xl font-bold text-green-600">
                    {investmentConditions[formData.investment_type].min_amount}€
                  </p>
                </div>
              </div>
              <p className="text-xs text-green-700 mt-3">
                {investmentConditions[formData.investment_type].description}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant à investir (€) *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={`Minimum ${investmentConditions[formData.investment_type].min_amount}€`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Informations complémentaires (optionnel)
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Nom du portefeuille, objectif d'investissement..."
              />
            </div>

            <button
              type="submit"
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Créer l'investissement
            </button>
          </form>
        </div>
      )}

      {/* Investments List */}
      <div className="grid gap-6">
        {investments.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 border border-gray-200 text-center">
            <p className="text-gray-500">Aucun investissement pour le moment</p>
          </div>
        ) : (
          investments.map((inv) => (
            <div
              key={inv.id}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">📈</span>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{inv.description}</h3>
                    <p className="text-sm text-gray-500">
                      {inv.investment_type} • 
                      <span className="ml-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                        {inv.status}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">
                    {inv.current_value?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </p>
                  <p className="text-sm text-green-600 font-semibold">
                    ROI: {inv.roi?.toFixed(2)}%
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-xs text-gray-600">Montant initial</p>
                  <p className="font-semibold">{inv.amount?.toFixed(2)} €</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Taux d'intérêt</p>
                  <p className="font-semibold text-green-600">{inv.interest_rate?.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Échéance</p>
                  <p className="font-semibold">
                    {inv.maturity_date ? new Date(inv.maturity_date).toLocaleDateString('fr-FR') : 
                     `${Math.round((inv.created_at ? (new Date(inv.created_at).getTime() + (inv.maturity_months || 0) * 30 * 24 * 60 * 60 * 1000 - Date.now()) / (30 * 24 * 60 * 60 * 1000) : 0))} mois`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Date de création</p>
                  <p className="font-semibold">
                    {new Date(inv.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Risk Warning */}
      <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-6 mt-6">
        <div className="flex items-start gap-3">
          <span className="text-3xl">⚠️</span>
          <div>
            <h3 className="text-lg font-bold text-yellow-900 mb-2">Avertissement sur les risques</h3>
            <p className="text-sm text-yellow-800">
              Les investissements financiers présentent des risques de perte en capital. Les fluctuations des marchés financiers peuvent affecter 
              la valeur de vos investissements à la hausse comme à la baisse. Les performances passées ne garantissent pas les résultats futurs. 
              Investir dans des produits financiers nécessite une compréhension des risques associés et de votre tolérance au risque. Nous vous 
              recommandons de diversifier vos investissements et de consulter un conseiller financier si nécessaire.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Investments;
